import { useAppStateStore } from '@/features/application'
import { useDialog } from '@/features/interface/dialog'
import * as api from '@/features/services/demande'
import { isNotNullOrUndefined } from '@/features/shared/tools'
import { useFavorisStore } from '@/features/interface/favoris/favorisStore.ts'
import { demandeToLocation, locationToParams } from '@/features/application/locations'
import { navigateTo, type NavigateToDemande, type NavigateToDemandeExtranet } from '@/router/navigateTo'
import type { DRRechercheModule } from '@/features/interface/recherches/dr/DRType.ts'
import type { RechercheResultDemandeRemboursementDTO } from '@/features/api/extranet/dto/Demande/Recherche/Remboursements'
import type { RechercheResultDossierRemboursementDTO } from '@/features/api/extranet/dto/Dossier/Recherche/Remboursements'

export type DRFAction = 'show-dr' | 'edit-dr' | 'clone-dr' | 'delete-dr'
export type DRFActionEnable = { [Property in DRFAction]: boolean }

export function useRechercheDRActions(typeDemande: DRRechercheModule) {
  const { autoriseEcriture } = useAppStateStore()
  const { showDialog } = useDialog()
  const { addFavori } = useFavorisStore()

  const storeRecherche = locationToParams({ to: 'recherche', domaine: 'dr', module: typeDemande }).useStore()

  async function deleteDemandeDR(
    item: RechercheResultDemandeRemboursementDTO | RechercheResultDossierRemboursementDTO,
  ) {
    if (!estDemande(item) || item.niveauPermission !== 'Ecriture') return

    const result = await showDialog({
      message: 'Confirmez-vous la suppression du brouillon ?',
      type: 'YesNo',
      title: 'Supprimer un brouillon',
    })

    if (result === 'Yes') {
      const location = demandeToLocation({
        typeDemande: 'Remboursement',
        dispositif: item.dispositif!,
        origine: 'extranet',
        id: item.id,
      }) as NavigateToDemandeExtranet

      await api.demandeService.deleteDemande(location)
      await storeRecherche.reloadData()
    }
  }

  function navigateToDR(item: RechercheResultDemandeRemboursementDTO | RechercheResultDossierRemboursementDTO) {
    const location: NavigateToDemande = estDemande(item) ? {
      to: 'detail',
      domaine: 'dr',
      module: 'demande',
      id: item.id
    } : {
      to: 'detail',
      domaine: 'dr',
      module: 'dossier',
      code: item.code,
    }
    return navigateTo(location)
  }

  function estDemande(
    item: RechercheResultDemandeRemboursementDTO | RechercheResultDossierRemboursementDTO,
  ): item is RechercheResultDemandeRemboursementDTO {
    return 'id' in item
  }

  function estDossier(
    item: RechercheResultDemandeRemboursementDTO | RechercheResultDossierRemboursementDTO,
  ): item is RechercheResultDossierRemboursementDTO {
    return isNotNullOrUndefined(item.code)
  }

  const getItemState = (item: RechercheResultDemandeRemboursementDTO | RechercheResultDossierRemboursementDTO) => {
    const editDR =
      estDemande(item) &&
      item.statut === 'SaisieAdherent' &&
      item.niveauPermission === 'Ecriture'

    const deleteDR = editDR
    const showDR = !editDR
    const cloneDR = estDemande(item) && autoriseEcriture('DR')

    return {
      'edit-dr': editDR,
      'show-dr': showDR,
      'clone-dr': cloneDR,
      'delete-dr': deleteDR,
    }
  }

  return {
    addFavori,
    deleteDemandeDR,
    navigateToDR,
    estDemande,
    estDossier,
    getItemState,
  }
}
